import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  PopoverCloseButton,
  Text,
  useBoolean,
  useOutsideClick,
  FormControl,
  FormLabel,
  Divider,
  useMediaQuery,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import BranchIcon from 'components/Icons/BranchIcon';
import InfoIconComponent from 'components/Icons/InfoIcon';
import PlanPermission from 'container/PlanPermission';
import DeleteModal from 'components/Modals/DeleteModal';
import { fetchAllScenarios, getAllScenarios } from 'redux/ScenarioSlice';
import { Scenario } from 'models/response/scenario.interface';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  getSelectedDashboardScenario,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  getCompareDashboardScenario,
  getDashboard,
  fetchAllDataValues,
  getSelectedPage,
  getTimeFilter,
  getIsPageBarOpen,
} from 'redux/DashboardSliceV2';
import { DeleteScenarios, DuplicateScenarios } from 'redux/BuilderModeSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';

import ScenarioForm from './ScenarioForm';

export interface SelectScenarioProperties {
  refetchAllInstruments?: any;
  planPermission: boolean;
}

const SelectScenario = ({ planPermission }: SelectScenarioProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const compareReference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const timeFilter = useSelector(getTimeFilter);
  const scenarios = useSelector(getAllScenarios);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const { modelId } = useParams();
  const [scenariosPopover, setScenariosPopover] = useBoolean();
  const [compareScenariosPopover, setCompareScenariosPopover] = useBoolean();
  const [scenarioComparisonModal, setScenarioComparisonModal] = useBoolean();
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [isMobile] = useMediaQuery('(max-width: 681px)');
  const [isSmallDevice] = useMediaQuery('(max-width: 1100px)');
  const isPageBarOpen = (useSelector(getIsPageBarOpen) && isSmallDevice) || isSmallDevice;
  const [addScenarioPopover, setAddScenarioPopover] = useBoolean();
  const [displayInfo, setDisplayInfo]: any = useState(null);
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [editScenarioData, setEditScenarioData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isPermissionComponent, setIsPermissionComponent] = useState(false);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);

  const selectCompareScenario = (scenario: any) => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[dashboard.model_id] || {};
    modalFilters.compareScenario = scenario;
    localStorage.setItem(
      'global-modal-filters',
      JSON.stringify({ ...data, [dashboard.model_id]: modalFilters }),
    );
    dispatch(selectCompareDashboardScenario(scenario));
    if (
      dashboard?.id &&
      selectedPage?.id &&
      selectedScenario?.id &&
      timeFilter?.start &&
      timeFilter?.end
    ) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage.id,
          scenarioId: selectedScenario.id,
        }),
      );
    }
  };

  const selectScenario = (scenario: any) => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[dashboard.model_id] || {};
    modalFilters.scenario = scenario;
    localStorage.setItem(
      'global-modal-filters',
      JSON.stringify({ ...data, [dashboard.model_id]: modalFilters }),
    );
    dispatch(selectDashboardScenario(scenario));
    if (compareScenario?.id === scenario?.id) {
      selectCompareScenario(null);
    }
    if (dashboard?.id) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage?.id,
          scenarioId: scenario?.id,
          start_date: timeFilter?.start || '',
          end_date: timeFilter?.end || '',
        }),
      );
    }
  };

  const deleteScenario = async (scenarioId: number) => {
    dispatch(DeleteScenarios(scenarioId));
  };

  const onDeleteModel = async () => {
    const wasSelectedScenario = selectedScenario?.id === deleteIdModal;
    const wasCompareScenario = compareScenario?.id === deleteIdModal;

    await deleteScenario(deleteIdModal);
    const fetchResult = await dispatch(fetchAllScenarios(modelId));
    const updatedScenarios = fetchResult?.payload || scenarios;

    await setIsDeleteModal(false);
    await setDeleteIdModal(null);
    await setDeleteNameModal('');

    if (wasSelectedScenario) {
      const baseScenario = updatedScenarios.find((scenario: Scenario) => scenario?.is_base);
      const scenarioToSelect = baseScenario || updatedScenarios[0];
      if (scenarioToSelect) {
        selectScenario(scenarioToSelect);
      }
    }

    if (wasCompareScenario) {
      const remainingScenarios = updatedScenarios.filter(
        (scenario: Scenario) => scenario?.id !== selectedScenario?.id,
      );
      if (remainingScenarios.length > 0) {
        const nextCompare =
          remainingScenarios.find((scenario: Scenario) => scenario?.is_base) ||
          remainingScenarios[0];
        if (nextCompare) {
          selectCompareScenario(nextCompare);
        } else {
          selectCompareScenario(null);
        }
      } else {
        selectCompareScenario(null);
      }
    }
  };

  useEffect(() => {
    if (scenarios?.length === 1 && compareScenario) {
      selectCompareScenario(null);
    }
  }, [scenarios?.length, compareScenario]);

  useOutsideClick({
    ref: reference,
    handler: () => {
      setScenariosPopover.off();
      setDisplayInfo(null);
      setRenameItemId(0);
      setAddScenarioPopover.off();
      setEditScenarioData(null);
      setIsEdit(false);
    },
  });

  useOutsideClick({
    ref: compareReference,
    handler: () => {
      setCompareScenariosPopover.off();
    },
  });

  const toggleInfoPopup = (event: any, id: number | null) => {
    event.stopPropagation();
    if (displayInfo === id) {
      return setDisplayInfo(null);
    }
    return setDisplayInfo(id);
  };

  const renameItem = (event: any, scenario: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setDisplayInfo(null);
    if (renameItemId === scenario?.id) {
      setRenameItemId(0);
      setEditScenarioData(null);
      setIsEdit(false);
    }
    setRenameItemId(scenario?.id);
    setAddScenarioPopover.on();
    setEditScenarioData(scenario);
    setIsEdit(true);
  };

  const copyScenario = async (event: any, item: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setDisplayInfo(null);
    const requestBody = {
      scenarioId: item?.id,
      data: {
        name: `${item?.name} - Copy`,
      },
      toaster: {
        successMessage: 'Successfully duplicated scenario',
        errorMessage: 'Failed to duplicate',
      },
    };
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      await dispatch(DuplicateScenarios(requestBody));
      await dispatch(fetchAllScenarios(modelId));
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setIsPermissionComponent(true);
      setScenariosPopover.on();
    } else {
      setIsPermissionComponent(false);
      await dispatch(DuplicateScenarios(requestBody));
      await dispatch(fetchAllScenarios(modelId));
    }
  };

  const deleteScenarioModal = async (event: any, id: number, name: any) => {
    event.stopPropagation();
    if (planPermission) {
      return;
    }
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteNameModal(name);
  };

  const handleCancel = () => {
    setAddScenarioPopover.off();
    setEditScenarioData(null);
    setIsEdit(false);
  };

  const OpenPopover = () => {
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setAddScenarioPopover.on();
      setIsPermissionComponent(true);
    } else if (planPermission === true) {
      setAddScenarioPopover.off();
      setIsPermissionComponent(false);
    } else {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
      setDisplayInfo(null);
    }
    setEditScenarioData(null);
    setIsEdit(false);
  };

  useEffect(() => {
    setIsPermissionComponent(false);
  }, [UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model]);

  const scenariosLength = scenarios?.length ?? 0;

  return (
    <Flex alignItems='center' gap={0} maxWidth={'400px'} width={'auto'}>
      {scenarios?.length > 0 && (
        <Flex
          alignItems='center'
          gap={0}
          backgroundColor={'#332663'}
          padding={'.5rem 1rem'}
          borderRadius={'sm'}
          height={'40px'}
          cursor={planPermission ? 'not-allowed' : 'pointer'}
          onClick={planPermission ? undefined : setScenarioComparisonModal.on}
          opacity={planPermission ? 0.6 : 1}
        >
          <Flex
            textAlign='left'
            alignSelf='left'
            height={'100%'}
            px={1}
            transition='all 0.3s ease-out'
            justifyContent='space-between'
            alignItems='center'
            ps={0}
            gap={isPageBarOpen ? 0 : 2}
            backgroundColor={'transparent'}
            borderRadius={'sm'}
            minWidth={'auto'}
            flex={1}
            title={`${selectedScenario?.name || 'No scenario selected'} ${
              compareScenario ? `vs ${compareScenario?.name || ''}` : ''
            }`}
          >
            {isPageBarOpen ? (
              <BranchIcon height={7} width={7} />
            ) : (
              <>
                <Flex width='100%' alignItems='center' title={selectedScenario?.name}>
                  <Box width='8px' height='8px' borderRadius='50%' bg={'#FF6B35'} mr={2} />
                  <Text
                    alignContent='center'
                    color='white'
                    fontSize='xs'
                    whiteSpace='nowrap'
                    overflow='hidden'
                    textOverflow={'ellipsis'}
                    flex={1}
                  >
                    {selectedScenario?.name || 'Select Scenario'}
                  </Text>
                </Flex>
                {compareScenario && (
                  <>
                    <Text color='white' fontSize='xs' fontWeight='600' px={1}>
                      vs
                    </Text>
                    <Flex width='100%' alignItems='center' title={compareScenario?.name}>
                      <Box width='8px' height='8px' borderRadius='50%' bg='#6562CF' mr={2} />
                      <Text
                        alignContent='center'
                        color='white'
                        fontSize='xs'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow={'ellipsis'}
                        flex={1}
                      >
                        {compareScenario?.name}
                      </Text>
                    </Flex>
                  </>
                )}
              </>
            )}
            <ChevronDownIcon boxSize={{ base: 6 }} color='white' ml={2} />
          </Flex>
        </Flex>
      )}

      {/* Scenario Comparison Popover */}
      <Popover
        placement='bottom-start'
        closeOnBlur={true}
        isOpen={scenarioComparisonModal}
        onOpen={planPermission ? undefined : setScenarioComparisonModal.on}
        onClose={setScenarioComparisonModal.off}
      >
        <PopoverTrigger>
          <Box as='span' />
        </PopoverTrigger>
        <PopoverContent
          ml={{ base: '5', md: '3' }}
          filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
          bg='white'
          border='none'
          maxWidth='80vw'
          borderRadius='14px'
        >
          <PopoverCloseButton />
          <Box pt={4} pb={1} px={5}>
            <Text color='#6562CF' fontSize={'md'} fontWeight={'600'}>
              Scenario Comparison
            </Text>
            <Text color='#8B8AAB' fontSize={'11px'} mt={1}>
              Choose which scenarios to compare in variance cards and KPIs
            </Text>
          </Box>
          <PopoverBody px={5} pb={4}>
            <Flex direction='column' gap={4}>
              {/* Base Scenario Dropdown */}
              <FormControl>
                <FormLabel color='black' fontSize={'xs'} fontWeight={'500'} mb={2}>
                  Base Scenario
                </FormLabel>
                <Popover
                  placement='bottom-start'
                  closeOnBlur={false}
                  isOpen={scenariosPopover}
                  onOpen={planPermission ? undefined : setScenariosPopover.on}
                  onClose={setScenariosPopover.off}
                >
                  <Box ref={reference}>
                    <PopoverTrigger>
                      <Flex
                        alignItems='center'
                        justifyContent='space-between'
                        border='1px solid #E2E8F0'
                        borderRadius='8px'
                        px={3}
                        py={2}
                        cursor={planPermission ? 'not-allowed' : 'pointer'}
                        _hover={planPermission ? {} : { borderColor: '#6562CF' }}
                        opacity={planPermission ? 0.6 : 1}
                      >
                        <Flex alignItems='center' gap={2} flex={1}>
                          <Box width='8px' height='8px' borderRadius='50%' bg='#FF6B35' />
                          <Text fontSize={'xs'} color='black'>
                            {selectedScenario?.name || 'Select Scenario'}
                          </Text>
                        </Flex>
                        <ChevronDownIcon boxSize={4} color='#8B8AAB' />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent
                      filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                      bg='white'
                      borderRadius='14px'
                      border='none'
                    >
                      <PopoverBody p={2}>
                        <Flex direction='column'>
                          {scenarios?.length > 0 &&
                            scenarios.map((scenario: Scenario, index: number) => (
                              <Flex
                                key={scenario.id}
                                alignItems='center'
                                justifyContent='space-between'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                borderRadius='14px'
                                px='1'
                                py='2'
                                onClick={
                                  planPermission
                                    ? undefined
                                    : () => {
                                        selectScenario(scenario);
                                        setScenariosPopover.off();
                                      }
                                }
                                bg={
                                  selectedScenario?.id === scenario?.id ? '#EFEEFE' : 'transparent'
                                }
                                _hover={planPermission ? {} : { bg: '#F5F5F5' }}
                                onDoubleClick={(event) => {
                                  event.stopPropagation();
                                  setRenameItemId(scenario?.id);
                                }}
                              >
                                <Box
                                  display={'flex'}
                                  color={'#8B89B1'}
                                  alignItems={'center'}
                                  w={'70%'}
                                  onClick={() => setDisplayInfo(null)}
                                >
                                  {selectedScenario?.id === scenario?.id && (
                                    <CheckIcon color='#6562CF' />
                                  )}
                                  <Text
                                    px='2'
                                    py='0'
                                    color='black'
                                    fontSize={'xs'}
                                    whiteSpace='nowrap'
                                    overflow='hidden'
                                    textOverflow={'ellipsis'}
                                  >
                                    {scenario?.name}
                                  </Text>
                                </Box>

                                <Flex alignItems='center' position='relative'>
                                  <Flex
                                    w='25px'
                                    height='25px'
                                    mr='15px'
                                    alignItems='center'
                                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                                    onClick={
                                      planPermission
                                        ? undefined
                                        : (event) => toggleInfoPopup(event, scenario.id)
                                    }
                                  >
                                    <InfoIconComponent width='20px' height='8px' />
                                  </Flex>
                                  <Flex
                                    bgColor='white'
                                    width='fit-content'
                                    position='absolute'
                                    display={displayInfo === scenario.id ? 'block' : 'none'}
                                    zIndex='dropdown'
                                    top={
                                      scenariosLength > 3 && index >= scenariosLength - 3
                                        ? '-100px'
                                        : '28px'
                                    }
                                    right='0'
                                    p='5px 0'
                                    filter='drop-shadow(0px 0px 15.998px rgba(0, 0, 0, 0.25))'
                                    borderRadius='4px'
                                  >
                                    <Box
                                      p='5px 15px'
                                      _hover={{
                                        background: '#EEF2F6',
                                        color: 'black',
                                      }}
                                    >
                                      <Text
                                        color='black'
                                        fontSize={'xs'}
                                        onClick={
                                          planPermission
                                            ? undefined
                                            : (event) => renameItem(event, scenario)
                                        }
                                      >
                                        Edit
                                      </Text>
                                    </Box>
                                    <Box
                                      p='5px 15px'
                                      _hover={{
                                        background: '#EEF2F6',
                                        color: 'black',
                                      }}
                                    >
                                      <Text
                                        color='black'
                                        fontSize={'xs'}
                                        onClick={
                                          planPermission
                                            ? undefined
                                            : (event) => copyScenario(event, scenario)
                                        }
                                      >
                                        Duplicate
                                      </Text>
                                    </Box>
                                    {Number(scenarios?.length) !== 1 && (
                                      <Box
                                        p='5px 15px'
                                        _hover={{
                                          background: '#EEF2F6',
                                          color: 'black',
                                        }}
                                      >
                                        <Text
                                          color='black'
                                          fontSize={'xs'}
                                          onClick={
                                            planPermission
                                              ? undefined
                                              : (event) => {
                                                  deleteScenarioModal(
                                                    event,
                                                    scenario?.id,
                                                    scenario?.name,
                                                  );
                                                }
                                          }
                                        >
                                          Delete
                                        </Text>
                                      </Box>
                                    )}
                                  </Flex>
                                </Flex>
                              </Flex>
                            ))}
                        </Flex>
                        <Divider orientation='horizontal' mt={'2'} />
                        <Popover
                          placement={isMobile ? 'bottom' : 'right-start'}
                          closeOnBlur={false}
                          isOpen={addScenarioPopover}
                          onOpen={planPermission ? undefined : OpenPopover}
                          onClose={setAddScenarioPopover.off}
                          isLazy
                          lazyBehavior='keepMounted'
                        >
                          <PopoverTrigger>
                            <Text
                              mt={scenarios?.length > 0 ? '2' : '0'}
                              color='#6562CF'
                              fontSize={'xs'}
                              fontWeight={'600'}
                              px={2}
                              cursor={planPermission ? 'not-allowed' : 'pointer'}
                            >
                              + New Scenario
                            </Text>
                          </PopoverTrigger>
                          <PopoverContent
                            p={5}
                            filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                            bg='white'
                            borderRadius='14px'
                          >
                            <ScenarioForm
                              onCancel={handleCancel}
                              editScenarioData={editScenarioData}
                              isEdit={isEdit}
                              setIsEdit={setIsEdit}
                            />
                          </PopoverContent>
                        </Popover>
                        {isPermissionComponent && (
                          <ScenarioPermission
                            ScenarioLimit={
                              UserSubscriptionLimitData?.subscription_features_limit
                                ?.scenario_per_model
                            }
                            CurrentScenarioLength={scenarios?.length}
                          />
                        )}
                      </PopoverBody>
                    </PopoverContent>
                  </Box>
                </Popover>
              </FormControl>

              {/* Compare Against Dropdown */}
              {scenarios?.length > 1 && (
                <FormControl>
                  <FormLabel color='black' fontSize={'xs'} fontWeight={'500'} mb={2}>
                    Compare Against
                  </FormLabel>
                  <Popover
                    placement='bottom-start'
                    closeOnBlur={false}
                    isOpen={compareScenariosPopover}
                    onOpen={planPermission ? undefined : setCompareScenariosPopover.on}
                    onClose={setCompareScenariosPopover.off}
                  >
                    <Box ref={compareReference}>
                      <PopoverTrigger>
                        <Flex
                          alignItems='center'
                          justifyContent='space-between'
                          border='1px solid #E2E8F0'
                          borderRadius='8px'
                          px={3}
                          py={2}
                          cursor={planPermission ? 'not-allowed' : 'pointer'}
                          _hover={planPermission ? {} : { borderColor: '#6562CF' }}
                          opacity={planPermission ? 0.6 : 1}
                        >
                          <Flex alignItems='center' gap={2} flex={1}>
                            <Box width='8px' height='8px' borderRadius='50%' bg='#6562CF' />
                            <Text fontSize={'xs'} color='black'>
                              {compareScenario?.name || 'Select Scenario'}
                            </Text>
                          </Flex>
                          <ChevronDownIcon boxSize={4} color='#8B8AAB' />
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent
                        filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                        bg='white'
                        borderRadius='14px'
                        border='none'
                      >
                        <PopoverBody p={2}>
                          <Flex direction='column'>
                            {scenarios?.length > 0 &&
                              scenarios.map((scenario: Scenario) => {
                                if (selectedScenario?.id === scenario?.id) {
                                  return null;
                                }
                                return (
                                  <Flex
                                    key={scenario.id}
                                    alignItems='center'
                                    justifyContent='space-between'
                                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                                    borderRadius='14px'
                                    px='1'
                                    py='2'
                                    onClick={
                                      planPermission
                                        ? undefined
                                        : () => {
                                            if (compareScenario?.id === scenario?.id) {
                                              selectCompareScenario(null);
                                            } else {
                                              selectCompareScenario(scenario);
                                            }
                                            setCompareScenariosPopover.off();
                                          }
                                    }
                                    bg={
                                      compareScenario?.id === scenario?.id
                                        ? '#EFEEFE'
                                        : 'transparent'
                                    }
                                    _hover={planPermission ? {} : { bg: '#F5F5F5' }}
                                  >
                                    <Box
                                      display={'flex'}
                                      color={'#8B89B1'}
                                      alignItems={'center'}
                                      w={'70%'}
                                    >
                                      {compareScenario?.id === scenario?.id && (
                                        <CheckIcon color='#6562CF' />
                                      )}
                                      <Text px='2' py='0' color='black' fontSize={'xs'}>
                                        {scenario?.name}
                                      </Text>
                                    </Box>
                                  </Flex>
                                );
                              })}
                            {compareScenario && (
                              <Flex
                                mt={2}
                                alignItems='center'
                                justifyContent='center'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                borderRadius='14px'
                                px='1'
                                py='2'
                                onClick={
                                  planPermission
                                    ? undefined
                                    : () => {
                                        selectCompareScenario(null);
                                        setCompareScenariosPopover.off();
                                      }
                                }
                                _hover={planPermission ? {} : { bg: '#EFEEFE' }}
                              >
                                <Text
                                  px={'2'}
                                  py={'0'}
                                  color={'#6562CF'}
                                  fontSize={'xs'}
                                  fontWeight={'600'}
                                >
                                  Clear Compare
                                </Text>
                              </Flex>
                            )}
                          </Flex>
                        </PopoverBody>
                      </PopoverContent>
                    </Box>
                  </Popover>
                </FormControl>
              )}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
    </Flex>
  );
};

export default PlanPermission(SelectScenario);
